import { Dialog } from "@headlessui/react";
import { Input } from "../components/ui/Input.jsx";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/Button.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/Select.jsx";
import { updateLead } from "../services/leadService.js";
import { fetchStages } from "../services/leadStageService.js";
import { useToast } from "../hooks/useToast.js";
import { useState, useEffect } from "react";

const convertTo24Hour = (time12h) => {
  if (!time12h) return "";
  const [time, modifier] = time12h.split(" ");
  let [hours, minutes] = time.split(":");
  hours = parseInt(hours, 10);
  if (modifier === "PM" && hours < 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;
  return `${hours.toString().padStart(2, "0")}:${minutes}`;
};

export default function AddNoteModal({
  isNoteModalOpen,
  setIsNoteModalOpen,
  noteComment,
  setNoteComment,
  saveNote,
  currentStage,
  setCurrentStage,
  leadId,
  onCommentAdded,
}) {
  const [noteDate, setNoteDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });
  const [noteTime, setNoteTime] = useState(() => {
    return new Date().toTimeString().slice(0, 5);
  });
  const [stages, setStages] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    if (isNoteModalOpen) {
      (async () => {
        try {
          const res = await fetchStages();
          setStages(res.data || []);
        } catch {
          setStages([]);
        }
      })();
    }
  }, [isNoteModalOpen]);

  const currentStageData = stages.find((s) => s.stage_key === currentStage);

  const handleSave = async () => {
    if (!noteComment.trim()) return;

    const isMeeting = currentStageData
      ? currentStageData.stage_key === "meeting"
      : currentStage === "meeting";

    // Validate meeting date/time
    if (isMeeting && (!noteDate || !noteTime)) {
      toast({
        title: "Meeting date & time required",
        description: "Please select date and time for the meeting.",
        variant: "destructive",
      });
      return;
    }

    const convertedTime = convertTo24Hour(noteTime);

    const commentText = isMeeting
      ? `${noteComment}\n\nMeeting: ${noteDate} at ${noteTime}`
      : noteComment;

    const commentPayload = [
      {
        comment_text: commentText,
        contact_channel: currentStage,
        comment_date: isMeeting ? noteDate : null,
        comment_time: isMeeting ? convertedTime : null,
      },
    ];

    const updatePayload = {
      comment: commentPayload,
      deal_status: currentStage,
    };

    // Set contact_time for meeting stage
    if (isMeeting && noteDate && noteTime) {
      updatePayload.contact_time = `${noteDate} ${convertedTime}:00`;
    }

    try {
      await updateLead(leadId, updatePayload);

      toast({
        title: "Updated successfully",
        description: `Lead stage changed to ${currentStageData?.stage_label || currentStage}.`,
      });

      setNoteComment("");
      setNoteDate("");
      setNoteTime("00:00");
      setIsNoteModalOpen(false);

      if (onCommentAdded) onCommentAdded();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to add comment. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Show date/time ONLY for meeting stage
  const isMeetingStage = currentStageData
    ? currentStageData.stage_key === "meeting"
    : currentStage === "meeting";

  return (
    <AnimatePresence>
      {isNoteModalOpen && (
        <Dialog
          static
          open={isNoteModalOpen}
          onClose={() => setIsNoteModalOpen(false)}
          className="fixed inset-0 z-50 flex flex-col justify-end"
        >
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            onClick={() => setIsNoteModalOpen(false)}
          />

          <motion.div
            key="add-note-modal"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 160, damping: 20, mass: 0.6 }}
            className="relative w-full bg-white rounded-t-3xl shadow-xl p-5 z-50"
          >
            <div
              className="bg-gray-400/70 absolute top-1 left-1/2 -translate-x-1/2 h-1.5 w-14 rounded-full cursor-pointer"
              onClick={() => setIsNoteModalOpen(false)}
            />

            <Dialog.Title className="text-base font-semibold text-gray-800 mb-4 text-center">
              Add Note
            </Dialog.Title>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Add Comment *
                </label>
                <Input
                  value={noteComment}
                  onChange={(e) => setNoteComment(e.target.value.trimStart())}
                  placeholder="Add your comment here"
                  className="h-9 text-sm border-blue-100/50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lead Stage
                </label>
                <Select value={currentStage} onValueChange={setCurrentStage}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {stages.map((stage) => (
                      <SelectItem key={stage.id} value={stage.stage_key}>
                        {stage.stage_label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isMeetingStage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meeting Date & Time *
                  </label>
                  <div className="flex gap-3">
                    <Input
                      type="date"
                      value={noteDate}
                      onChange={(e) => setNoteDate(e.target.value)}
                      className="h-9 text-sm"
                    />
                    <Input
                      type="time"
                      value={noteTime}
                      onChange={(e) => setNoteTime(e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setIsNoteModalOpen(false)}
                className="h-8 px-3 text-xs rounded-full border border-gray-200 text-gray-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!noteComment}
                className="h-8 px-4 text-xs rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391] text-white border border-transparent"
              >
                Save
              </Button>
            </div>
          </motion.div>
        </Dialog>
      )}
    </AnimatePresence>
  );
}
